import { useParams } from "react-router";
import { useEffect, useState } from "react";
import moment from "moment-timezone";
import {
    loadScorable,
    selectActiveScorableExists,
    selectHasAssignPermission,
    selectHasScorableLoaded,
    selectIsCalibration,
    selectIsEvaluationDifferent,
    selectIsScorableComplete,
    selectIsScorableFinished,
    selectIsSubmitAndAssign,
    selectReleaseDate,
    selectScorable,
    selectSideDrawerMode,
} from "features/scorable/scorableSlice";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import FormType from "enums/FormType";
import { SideDrawerMode } from "enums/SideDrawerMode";
import { Evaluation } from "features/scorable/Evaluation";
import { fetchTopics } from "features/topics/topicsSlice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { NotAuthorized } from "./NotAuthorized";
import setContextRoute from "react/utils/setContextRoute";
import { getDecodedQueryParamsFromWindow } from "util/uriUtil";
import { selectForm } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { selectHasMultipleSelectQuestions } from "features/qualityForm/qualityFormValidations";
import toastService from "services/toastService";

interface IEvaluationRouteProps {
    context: "admin" | "agent";
}

export function EvaluationRoute({ context }: IEvaluationRouteProps) {
    const { conversationId, evaluationId } = useParams();

    const isEvaluationDifferent = useAppSelector(selectIsEvaluationDifferent);
    const isSubmitAndAssign = useAppSelector(selectIsSubmitAndAssign);
    const scorableId = useAppSelector(selectScorable)?.id;
    const [sideDrawerMode, setSideDrawerMode] = useState(SideDrawerMode.OFF);
    const requestedSideDrawerMode = useAppSelector((state) => {
        return selectSideDrawerMode(state) === SideDrawerMode.ASSIGN;
    });

    const dispatch = useAppDispatch();
    useEffect(() => {
        if (conversationId && evaluationId) {
            dispatch(
                loadScorable({
                    formType: FormType.EVALUATION,
                    conversationId,
                    evaluationId,
                })
            );
            dispatch(fetchTopics());
            if (isEvaluationDifferent && scorableId && isSubmitAndAssign) {
                setContextRoute(
                    conversationId,
                    scorableId,
                    context === "admin",
                    requestedSideDrawerMode
                );
            }
        }
    }, [
        dispatch,
        conversationId,
        evaluationId,
        isEvaluationDifferent,
        isSubmitAndAssign,
    ]);

    const assignmentPanelQueryParam =
        getDecodedQueryParamsFromWindow().assignmentPanel === "true";
    const now = moment();
    const hasAssignPermission = useAppSelector(selectHasAssignPermission);
    const isNotCalibration = !useAppSelector(selectIsCalibration);
    const activeScorableExists = useAppSelector(selectActiveScorableExists);
    const isScorableFinished = useAppSelector(selectIsScorableFinished);
    const releaseDate = useAppSelector(selectReleaseDate);
    const shouldShowAssignSideDrawer =
        context === "admin" &&
        assignmentPanelQueryParam &&
        hasAssignPermission &&
        isNotCalibration &&
        activeScorableExists &&
        (!isScorableFinished ||
            releaseDate === undefined ||
            moment(releaseDate).isAfter(now));
    useEffect(() => {
        if (shouldShowAssignSideDrawer) {
            setSideDrawerMode(SideDrawerMode.ASSIGN);
            const searchParams = new URLSearchParams(window.location.hash);
            searchParams.set("assignmentPanel", "false");
            window.location.hash = decodeURIComponent(searchParams.toString());
        }
    }, [
        context,
        assignmentPanelQueryParam,
        hasAssignPermission,
        isNotCalibration,
        activeScorableExists,
        isScorableFinished,
    ]);

    const isMultipleSelectFTEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.MULTIPLE_SELECT);
    });
    const hasMultipleSelectQuestions = useAppSelector(
        selectHasMultipleSelectQuestions
    );
    const cannotOpenEvaluationWithMSQ =
        !isMultipleSelectFTEnabled && hasMultipleSelectQuestions;
    const [hasShownToast, setHasShownToast] = useState(false);

    useEffect(() => {
        if (cannotOpenEvaluationWithMSQ && !hasShownToast) {
            toastService.notifyMinorAlert("evaluation.failToOpen.title", {
                bodyMessageKey: "evaluation.failToOpen.message",
            });
            setHasShownToast(true);
        }
    }, [cannotOpenEvaluationWithMSQ, hasShownToast]);

    const loadingHasFinished = useAppSelector(selectHasScorableLoaded);
    const isNotPublished = !useAppSelector(selectIsScorableComplete);
    const evaluationFailedToLoad = !useAppSelector(selectForm);

    return loadingHasFinished &&
        ((isNotPublished && context === "agent") ||
            evaluationFailedToLoad ||
            cannotOpenEvaluationWithMSQ) ? (
        <NotAuthorized />
    ) : (
        <Evaluation
            context={context}
            sideDrawerMode={sideDrawerMode}
            setSideDrawerMode={setSideDrawerMode}
        />
    );
}
